import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, FolderPlus, File, Folder, Eye, Lock, Archive, RotateCcw } from 'lucide-react';

interface FileItem {
  type: 'file' | 'directory';
  name: string;
  path: string;
  size?: number;
  createdAt?: string;
  modifiedAt?: string;
  extension?: string;
  children?: FileItem[];
}

interface UploadResponse {
  message: string;
  files: Array<{
    filename: string;
    originalName: string;
    path: string;
    url: string;
    size: number;
    mimeType: string;
  }>;
}

const SecretFileManager: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentPath] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadFolder, setUploadFolder] = useState('');
  const [keepOriginalName, setKeepOriginalName] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const API_BASE = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:5001/api/secret-files' 
    : '/api/secret-files';

  // Try to authenticate with stored secret key on component mount
  useEffect(() => {
    const storedKey = localStorage.getItem('secretFileManagerKey');
    if (storedKey) {
      setSecretKey(storedKey);
      testAuthentication(storedKey);
    }
  }, []);

  const testAuthentication = async (key: string) => {
    try {
      const response = await fetch(`${API_BASE}?secret=${encodeURIComponent(key)}`);
      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('secretFileManagerKey', key);
        loadFiles();
      } else {
        setIsAuthenticated(false);
        localStorage.removeItem('secretFileManagerKey');
      }
    } catch (error) {
      console.error('Authentication test failed:', error);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secretKey.trim()) return;
    
    setLoading(true);
    await testAuthentication(secretKey);
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setSecretKey('');
    localStorage.removeItem('secretFileManagerKey');
    setFiles([]);
  };

  const loadFiles = async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}?secret=${encodeURIComponent(secretKey)}`);
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
      } else {
        console.error('Failed to load files');
      }
    } catch (error) {
      console.error('Error loading files:', error);
    }
    setLoading(false);
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;

    setLoading(true);
    try {
      const formData = new FormData();
      
      // Add all selected files
      Array.from(selectedFiles).forEach(file => {
        formData.append('files', file);
      });
      
      // Add options
      formData.append('folder', uploadFolder);
      formData.append('keepOriginal', keepOriginalName.toString());

      const response = await fetch(`${API_BASE}/upload?secret=${encodeURIComponent(secretKey)}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data: UploadResponse = await response.json();
        alert(`${data.message}`);
        loadFiles(); // Refresh file list
        setSelectedFiles(null);
        setUploadFolder('');
        
        // Reset the file input
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed');
    }
    setLoading(false);
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    try {
      const response = await fetch(`${API_BASE}/download/${filePath}?secret=${encodeURIComponent(secretKey)}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const error = await response.json();
        alert(`Download failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed');
    }
  };

  const handleDelete = async (filePath: string) => {
    if (!confirm(`Are you sure you want to delete ${filePath}?`)) return;

    try {
      const response = await fetch(`${API_BASE}/${filePath}?secret=${encodeURIComponent(secretKey)}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('File deleted successfully');
        loadFiles(); // Refresh file list
      } else {
        const error = await response.json();
        alert(`Delete failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed');
    }
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;

    try {
      const response = await fetch(`${API_BASE}/create-folder?secret=${encodeURIComponent(secretKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folderPath }),
      });

      if (response.ok) {
        alert('Folder created successfully');
        loadFiles(); // Refresh file list
        setNewFolderName('');
      } else {
        const error = await response.json();
        alert(`Folder creation failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Folder creation error:', error);
      alert('Folder creation failed');
    }
  };

  const handleBackup = async () => {
    if (!confirm('Create a backup of all uploads? This will download a zip file with all your files.')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/backup?secret=${encodeURIComponent(secretKey)}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Get filename from response headers or create default
        const contentDisposition = response.headers.get('content-disposition');
        const fileName = contentDisposition 
          ? contentDisposition.split('filename="')[1]?.split('"')[0] 
          : `uploads-backup-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
        
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        alert('Backup created successfully!');
      } else {
        const error = await response.json();
        alert(`Backup failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Backup error:', error);
      alert('Backup failed');
    }
    setLoading(false);
  };

  const handleValidateZip = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch(`${API_BASE}/validate-zip?secret=${encodeURIComponent(secretKey)}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const fileList = data.files.map((f: any) => f.name).join('\n- ');
        alert(`ZIP file is valid!\n\nFiles found: ${data.totalFiles}\n\nSample files:\n- ${fileList}${data.totalFiles > 10 ? '\n... and more' : ''}`);
        return true;
      } else {
        const error = await response.json();
        alert(`ZIP validation failed: ${error.message}`);
        return false;
      }
    } catch (error) {
      console.error('Validation error:', error);
      alert('ZIP validation failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please select a ZIP file for restore');
      return;
    }

    // First validate the ZIP file
    const isValid = await handleValidateZip(file);
    if (!isValid) {
      e.target.value = ''; // Reset file input
      return;
    }

    if (!confirm('Restore from backup? This will REPLACE all current uploads with the contents of the backup file. Are you sure?')) {
      e.target.value = ''; // Reset file input
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch(`${API_BASE}/restore?secret=${encodeURIComponent(secretKey)}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Restore completed successfully!\n${data.message}`);
        loadFiles(); // Refresh file list
      } else {
        const error = await response.json();
        console.error('Restore error details:', error);
        alert(`Restore failed: ${error.message}${error.details ? '\n\nDetails: ' + JSON.stringify(error.details, null, 2) : ''}`);
      }
    } catch (error) {
      console.error('Restore error:', error);
      alert('Restore failed');
    }
    
    // Reset file input
    e.target.value = '';
    setLoading(false);
  };

  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderFileTree = (items: FileItem[], level = 0) => {
    return items.map((item) => (
      <div key={item.path} style={{ marginLeft: `${level * 20}px` }} className="mb-2">
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
          <div className="flex items-center space-x-2">
            {item.type === 'directory' ? (
              <>
                <button 
                  onClick={() => toggleFolder(item.path)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Folder size={16} />
                </button>
                <span className="font-medium">{item.name}</span>
              </>
            ) : (
              <>
                <File size={16} className="text-gray-600" />
                <span>{item.name}</span>
                {item.size && (
                  <span className="text-sm text-gray-500">({formatFileSize(item.size)})</span>
                )}
              </>
            )}
          </div>
          
          <div className="flex space-x-2">
            {item.type === 'file' && (
              <>
                <button
                  onClick={() => handleDownload(item.path, item.name)}
                  className="text-green-600 hover:text-green-800"
                  title="Download"
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={() => window.open(
                    process.env.NODE_ENV === 'development' 
                      ? `http://localhost:5001/uploads/${item.path}` 
                      : `/uploads/${item.path}`, 
                    '_blank'
                  )}
                  className="text-blue-600 hover:text-blue-800"
                  title="View"
                >
                  <Eye size={16} />
                </button>
              </>
            )}
            <button
              onClick={() => handleDelete(item.path)}
              className="text-red-600 hover:text-red-800"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        
        {item.type === 'directory' && 
         item.children && 
         expandedFolders.has(item.path) && (
          <div className="mt-1">
            {renderFileTree(item.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <Lock className="text-gray-600 mr-2" size={24} />
            <h1 className="text-2xl font-bold text-gray-800">Secret File Manager</h1>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700 mb-2">
                Secret Key
              </label>
              <input
                type="password"
                id="secretKey"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter secret key"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Authenticating...' : 'Access File Manager'}
            </button>
          </form>
          
          <div className="mt-4 text-sm text-gray-600">
            <p>Default secret key: <code className="bg-gray-100 px-1 rounded">secret-upload-2024</code></p>
            <p className="mt-1">You can change this in the backend environment variables.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-800">Secret File Manager</h1>
            <div className="flex space-x-3">
              {/* Backup & Restore Buttons */}
              <button
                onClick={handleBackup}
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <Archive size={16} />
                <span>Backup All</span>
              </button>
              
              <label className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 cursor-pointer flex items-center space-x-2">
                <RotateCcw size={16} />
                <span>Restore</span>
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleRestore}
                  className="hidden"
                  disabled={loading}
                />
              </label>
              
              <div className="border-l border-gray-300 mx-2"></div>
              
              <button
                onClick={loadFiles}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Upload className="mr-2" size={20} />
              Upload Files
            </h2>
            
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div>
                <label htmlFor="fileInput" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Files
                </label>
                <input
                  type="file"
                  id="fileInput"
                  multiple
                  onChange={(e) => setSelectedFiles(e.target.files)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="uploadFolder" className="block text-sm font-medium text-gray-700 mb-2">
                  Folder (optional)
                </label>
                <input
                  type="text"
                  id="uploadFolder"
                  value={uploadFolder}
                  onChange={(e) => setUploadFolder(e.target.value)}
                  placeholder="e.g., images/products"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="keepOriginal"
                  checked={keepOriginalName}
                  onChange={(e) => setKeepOriginalName(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="keepOriginal" className="text-sm text-gray-700">
                  Keep original filename
                </label>
              </div>
              
              <button
                type="submit"
                disabled={loading || !selectedFiles}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Uploading...' : 'Upload Files'}
              </button>
            </form>
          </div>

          {/* Create Folder Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FolderPlus className="mr-2" size={20} />
              Create Folder
            </h2>
            
            <form onSubmit={handleCreateFolder} className="space-y-4">
              <div>
                <label htmlFor="newFolderName" className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  id="newFolderName"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="e.g., new-folder"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || !newFolderName.trim()}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                Create Folder
              </button>
            </form>
          </div>

          {/* Info Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Instructions</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>🔄 Backup & Restore:</strong></p>
              <p>• <span className="text-green-600">Backup All</span> - Download all files as ZIP</p>
              <p>• <span className="text-orange-600">Restore</span> - Upload ZIP to replace all files</p>
              
              <p className="pt-2"><strong>📁 File Management:</strong></p>
              <p>• Upload files to the /uploads directory</p>
              <p>• Files are organized in folders</p>
              <p>• Click folder icons to expand/collapse</p>
              <p>• Use download button to save files</p>
              <p>• Use view button to open files in browser</p>
              <p>• Delete files and folders with trash icon</p>
              <p>• 100MB max file size, 20 files per upload</p>
            </div>
          </div>
        </div>

        {/* File List */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-semibold mb-4">Files & Folders</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading files...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No files found. Upload some files to get started!
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {renderFileTree(files)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecretFileManager;